import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import startOfDay from 'date-fns/startOfDay'
import { usePageTitle } from '../../../actions/app'
import * as TicketSizesActions from '../../../actions/ticketSizes'
import { useAppDispatch } from '../../../actions/types'
import { Button, Centered, Column, Confirmation, Loader, Padding, SearchInput, Table } from '../../../components'
import { colors, Routes } from '../../../constants'
import { useAppSelector } from '../../../reducers'
import * as TicketSizesSelectors from '../../../reducers/ticketSizes'
import type { TicketSize } from '../../../types'
import { ArrayUtils } from '../../../utils'
import TicketSizeForm from './TicketSizeForm/TicketSizeForm'
import ticketSizeTableDisplay from './TicketSizes.table'
import useTicketSizeParams from './useTicketSizeParams'
import { useSize } from '../../../components/utils'

export type ExtendedTicketSize = TicketSize & { selected: boolean }

export default function TicketSizes() {
    const dispatch = useAppDispatch()
    const isWorking = useAppSelector(TicketSizesSelectors.isWorking)
    const isLoadingSelectedTicketSize = useAppSelector(TicketSizesSelectors.isLoadingSelectedTicketSize)
    const ticketSizes = useAppSelector(TicketSizesSelectors.selectTicketSizes)
    const selectedTicketSize = useAppSelector(TicketSizesSelectors.selectedTicketSize)
    const { t } = useTranslation()
    const history = useHistory()
    const { isMobile } = useSize()
    const { estate, ticketSizeId } = useTicketSizeParams()

    usePageTitle(ticketSizeId && selectedTicketSize ? t('ticket-size-title') : t('ticket-sizes-title'))

    const formRef = React.useRef<HTMLDivElement>(null)

    const [bulkSelectedTicketSizes, setBulkSelectedTicketSizes] = React.useState<number[]>([])
    const [isCreatingNewTicketSize, setIsCreatingNewTicketSize] = React.useState(false)
    const [searchTerm, setSearchTerm] = React.useState('')
    const today = React.useMemo(() => startOfDay(new Date()), [])

    React.useEffect(() => {
        dispatch(TicketSizesActions.fetchTicketSizes())
    }, [dispatch, estate])

    React.useEffect(() => {
        if (ticketSizeId) {
            setIsCreatingNewTicketSize(false)
            dispatch(TicketSizesActions.fetchTicketSize(ticketSizeId))

            if (isMobile) {
                formRef.current?.scrollIntoView()
            }
        } else if (isMobile) {
            window.scrollTo(0, 0)
        }
    }, [dispatch, estate, ticketSizeId, isMobile])

    const filteredTicketSizes = React.useMemo(() => {
        const normalizedTerm = searchTerm.trim().toLowerCase()
        const sortByDateStartAsc = (list: TicketSize[]) =>
            [...list].sort((a, b) => a.start_date.getTime() - b.start_date.getTime())

        if (normalizedTerm.length >= 3) {
            return sortByDateStartAsc(ticketSizes.filter(ticketSize => {
                const matchesType = ticketSize.size.toLowerCase().includes(normalizedTerm)
                const matchesSku = ticketSize.skus.some(sku => sku.toLowerCase().includes(normalizedTerm))
                const matchesStorecode = ticketSize.storecodes.some(code => code.toLowerCase().includes(normalizedTerm))
                return matchesType || matchesSku || matchesStorecode
            }))
        }

        return sortByDateStartAsc(ticketSizes.filter(ticketSize => {
            const start = startOfDay(ticketSize.start_date)
            const end = startOfDay(ticketSize.end_date)
            return (start <= today && end >= today) || start > today
        }))
    }, [ticketSizes, searchTerm, today])

    const extendedTicketSizes: ExtendedTicketSize[] = React.useMemo(() => {
        return filteredTicketSizes.map(ticketSize => {
            return {
                ...ticketSize,
                selected: bulkSelectedTicketSizes.includes(ticketSize.id),
            }
        })
    }, [filteredTicketSizes, bulkSelectedTicketSizes])

    if (isWorking) {
        return (
            <Padding top>
                <Centered horizontal>
                    <Loader />
                </Centered>
            </Padding>
        )
    }

    const onClickRow = (clickedTicketSizeId?: number) => {
        if (!clickedTicketSizeId) {
            history.replace(
                Routes.buildRoute(Routes.SELF_SERVE_TICKET_SIZES, { estate }),
            )
            return
        }

        const path = Routes.buildRoute(
            Routes.SELF_SERVE_TICKET_SIZES,
            { estate, ticketSize: clickedTicketSizeId },
        )

        if (isMobile) {
            history.push(path)
        } else {
            history.replace(path)
        }
    }

    const onSelectTicketSize = (id: number, isSelected: boolean) => {
        setBulkSelectedTicketSizes(prevSelection => {
            const itemIndex = prevSelection.findIndex(selectedId => selectedId === id)
            const isAlreadyIncluded = itemIndex > -1

            if (isSelected && !isAlreadyIncluded) {
                return [ ...prevSelection, id ]
            } else if (!isSelected && isAlreadyIncluded) {
                return ArrayUtils.removeItem(prevSelection, itemIndex)
            }

            return prevSelection
        })
    }

    const onDeleteTicketSizes = () => {
        const fn = async () => {
            const isDeletingSelected = !!ticketSizeId && bulkSelectedTicketSizes.includes(ticketSizeId)

            await Promise.all(bulkSelectedTicketSizes.map(async id => {
                await dispatch(TicketSizesActions.deleteTicketSize(id))
            }))

            if (isDeletingSelected) {
                history.replace(
                    Routes.buildRoute(Routes.SELF_SERVE_TICKET_SIZES, { estate })
                )
                dispatch(TicketSizesActions.fetchTicketSizes())
            } else {
                dispatch(TicketSizesActions.fetchTicketSizes())
            }

            setBulkSelectedTicketSizes([])
        }

        fn()
    }

    const onClickCreate = () => {
        setIsCreatingNewTicketSize(true)
        onClickRow()

        if (isMobile) {
            formRef.current?.scrollIntoView()
        }
    }

    return (
        <>
            <Padding>
                <Button
                    theme="forestGreen"
                    onClick={onClickCreate}
                >
                    {t('ticket-size-create')}
                </Button>
                <Padding top>
                    <div style={{ display: 'inline-flex', alignItems: 'center', position: 'relative', marginBottom: '10px' }}>
                        <SearchInput
                            width="280px"
                            value={searchTerm}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value)}
                        />
                        {searchTerm.length > 0 && (
                            <button
                                aria-label="Clear search"
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    color: colors.greydark,
                                    padding: 0,
                                    height: '16px',
                                    width: '16px',
                                    lineHeight: '16px',
                                }}
                            >
                                x
                            </button>
                        )}
                    </div>
                </Padding>
                <Confirmation
                    cancel={t('cancel')}
                    confirmation={t('ticket-size-remove-confirm-body', {
                        count: bulkSelectedTicketSizes.length,
                    })}
                    header={t('ticket-size-remove-confirm-header')}
                    onCancel={() => null}
                    onConfirm={onDeleteTicketSizes}
                    ok={t('ticket-size-remove-confirm-button')}
                    theme="danger"
                >
                    <Button
                        theme={bulkSelectedTicketSizes.length > 0 ? 'danger' : 'disabled'}
                        disabled={bulkSelectedTicketSizes.length === 0}
                    >
                        {bulkSelectedTicketSizes.length === 0 && t('ticket-size-remove-count-none')}
                        {bulkSelectedTicketSizes.length > 0 && t('ticket-size-remove-count', { count: bulkSelectedTicketSizes.length })}
                    </Button>
                </Confirmation>
            </Padding>
            <Column width={isMobile ? '100%' : '50%'}>
                <Table
                    data={extendedTicketSizes}
                    display={
                        ticketSizeTableDisplay({ onSelectTicketSize, t })
                    }
                    keyField="id"
                    onClickRow={onClickRow}
                    hideDeviceButtons
                    selectedItem={ticketSizeId}
                />
            </Column>
            <Column
                ref={formRef}
                width={isMobile ? '100%' : '50%'}
                color={colors.bluedarkest}
            >
                {!selectedTicketSize && isLoadingSelectedTicketSize && (
                    <Padding top>
                        <Centered horizontal>
                            <Loader />
                        </Centered>
                    </Padding>
                )}
                {(isCreatingNewTicketSize || !selectedTicketSize) && <TicketSizeForm />}
                {!isCreatingNewTicketSize && selectedTicketSize && <TicketSizeForm ticketSize={selectedTicketSize} />}
            </Column>
        </>
    )
}
